import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import API, { fmt, PROD_STATUS } from '../utils/api';
import Pagination from '../components/Pagination';

const STATUS_COLOR = { pending:'badge-warning', in_progress:'badge-primary', ready:'badge-success', delivered:'badge-gray' };
const STATUS_COLS  = ['pending','in_progress','ready','delivered'];
const SHORTAGE_REASONS = ['مادة ناقصة في المخزون','عطل في الإنتاج','خطأ تصنيع','طلب العميل التعديل','سبب آخر'];

/* ══════════════════════════════════════════
   مودال تعديل / تأكيد الكميات
══════════════════════════════════════════ */
function ActualQtyModal({ order, mode = 'edit', onClose, onSaved }) {
  const [items,        setItems]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(order.expected_delivery_date || '');
  const [prodNotes,    setProdNotes]    = useState(order.production_notes || '');

  useEffect(() => {
    Promise.all([
      API.get(`/orders/${order.id}/production-items`),
      !order.expected_delivery_date ? API.get(`/orders/${order.id}/suggested-delivery`).catch(()=>null) : Promise.resolve(null)
    ]).then(([itemsRes, deliveryRes]) => {
      setItems(itemsRes.data.map(i => ({
        ...i,
        actual_quantity: i.actual_quantity ?? i.quantity,
        shortage_reason: i.shortage_reason || '',
        shortage_notes:  i.shortage_notes  || '',
      })));
      if (deliveryRes?.data?.suggested_date && !order.expected_delivery_date) {
        setDeliveryDate(deliveryRes.data.suggested_date);
      }
      setLoading(false);
    });
  }, [order.id]);

  const update = (idx, field, val) =>
    setItems(prev => { const a = [...prev]; a[idx] = { ...a[idx], [field]: val }; return a; });

  const save = async (markReady = false) => {
    setSaving(true);
    try {
      await API.put(`/orders/${order.id}/production-actual`, {
        items: items.map(i => ({
          id: i.id,
          actual_quantity: +i.actual_quantity,
          shortage_reason: i.shortage_reason,
          shortage_notes:  i.shortage_notes,
        })),
        expected_delivery_date: deliveryDate,
        production_notes: prodNotes,
      });
      if (markReady) {
        await API.put(`/orders/${order.id}/production`, {
          production_status: 'ready',
          production_notes: prodNotes,
        });
      }
      onSaved();
      onClose();
    } catch(e) {
      alert(e.response?.data?.error || 'حدث خطأ أثناء الحفظ');
    } finally { setSaving(false); }
  };

  const newSubtotal = items.reduce((s, i) => s + (+i.actual_quantity * +i.unit_price), 0);
  const origSubtotal = items.reduce((s, i) => s + (+i.quantity * +i.unit_price), 0);
  const valueDiff = newSubtotal - origSubtotal;
  const totalShortage = items.reduce((s, i) => s + Math.max(0, +i.quantity - +i.actual_quantity), 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">
            {mode === 'confirm' ? '✅ تأكيد الكميات النهائية قبل الإغلاق' : '✏️ تعديل الكميات الفعلية'}
            {' — '}{order.order_number}
          </span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {loading ? <div className="loading"><div className="spinner"/><span>جاري التحميل...</span></div> : (<>
            {mode === 'confirm' && (
              <div className="alert alert-warning" style={{ marginBottom: 14 }}>
                ⚠️ قبل تغيير الحالة لـ "جاهز للتسليم" — تأكد من الكميات الفعلية المنتجة أدناه.
              </div>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
              <div style={{ background:'#f8f9fb', borderRadius:8, padding:'10px 14px', border:'1px solid var(--border)' }}>
                <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>العميل</div>
                <div style={{ fontWeight:700 }}>{order.customer_name}</div>
              </div>
              <div style={{ background:'#f8f9fb', borderRadius:8, padding:'10px 14px', border:'1px solid var(--border)' }}>
                <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginBottom:3 }}>تاريخ التسليم المتوقع</div>
                <input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)}
                  style={{ border:'none', background:'transparent', fontFamily:'inherit', fontWeight:700, padding:0, width:'100%', fontSize:'0.95rem' }} />
              </div>
            </div>
            <div style={{ overflowX:'auto', marginBottom:14 }}>
              <table style={{ minWidth: 580 }}>
                <thead>
                  <tr>
                    <th>المنتج</th><th>الرائحة</th>
                    <th style={{ textAlign:'center' }}>مطلوب</th>
                    <th style={{ textAlign:'center', color:'var(--primary)' }}>أُنتج فعلاً ✏️</th>
                    <th>سبب النقص</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const shortage = +item.actual_quantity < +item.quantity;
                    return (
                      <tr key={item.id} style={{ background: shortage ? '#fff7ed' : undefined }}>
                        <td className="font-bold" style={{ fontSize:'0.875rem' }}>{item.finished_product_name}</td>
                        <td>
                          {item.flavor_name
                            ? <span style={{ background:'var(--primary)', color:'white', borderRadius:20, padding:'2px 9px', fontSize:'0.75rem' }}>🌸 {item.flavor_name}</span>
                            : <span style={{ color:'var(--text-muted)' }}>—</span>}
                        </td>
                        <td style={{ textAlign:'center', color:'var(--text-muted)', fontWeight:700 }}>{item.quantity}</td>
                        <td style={{ textAlign:'center' }}>
                          <input type="number" value={item.actual_quantity}
                            onChange={e => update(idx, 'actual_quantity', e.target.value)}
                            min={0}
                            style={{
                              width: 90, textAlign:'center', fontWeight:800, fontSize:'1rem',
                              border: `2px solid ${shortage ? 'var(--warning)' : 'var(--success)'}`,
                              borderRadius: 8, padding:'6px',
                              background: shortage ? '#fef3c7' : '#dcfce7',
                              color: shortage ? '#d97706' : '#16a34a',
                            }}
                          />
                        </td>
                        <td>
                          {shortage ? (
                            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                              <select value={item.shortage_reason} onChange={e => update(idx, 'shortage_reason', e.target.value)}
                                style={{ fontSize:'0.8rem', padding:'5px 8px' }}>
                                <option value="">اختر السبب</option>
                                {SHORTAGE_REASONS.map(r => <option key={r}>{r}</option>)}
                              </select>
                              <input placeholder="ملاحظة..." value={item.shortage_notes}
                                onChange={e => update(idx, 'shortage_notes', e.target.value)}
                                style={{ fontSize:'0.8rem', padding:'5px 8px' }} />
                            </div>
                          ) : <span style={{ color:'var(--success)', fontSize:'0.85rem' }}>✅ مكتمل</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {valueDiff !== 0 && (
              <div style={{ background: valueDiff < 0 ? '#fee2e2' : '#dcfce7', border: `1px solid ${valueDiff < 0 ? '#fca5a5' : '#86efac'}`, borderRadius:8, padding:'10px 16px', marginBottom:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:'0.875rem', color: valueDiff < 0 ? 'var(--danger)' : 'var(--success)' }}>
                    {valueDiff < 0 ? '📉 سيتم خفض قيمة الطلب' : '📈 سيتم رفع قيمة الطلب'}
                  </div>
                  <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:2 }}>
                    الإجمالي الأصلي: <strong>$ {origSubtotal.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</strong>
                    {' → '}
                    الإجمالي الجديد: <strong>$ {newSubtotal.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</strong>
                  </div>
                </div>
                <div style={{ fontWeight:800, fontSize:'1.1rem', color: valueDiff < 0 ? 'var(--danger)' : 'var(--success)' }}>
                  {valueDiff < 0 ? '-' : '+'} $ {Math.abs(valueDiff).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                </div>
              </div>
            )}
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>ملاحظات الإنتاج</label>
              <textarea value={prodNotes} onChange={e => setProdNotes(e.target.value)} rows={2} placeholder="ملاحظات عامة..." />
            </div>
            {totalShortage > 0 ? (
              <div style={{ background:'#fef3c7', border:'1px solid #fcd34d', borderRadius:8, padding:'10px 14px', fontSize:'0.85rem', color:'#92400e' }}>
                ⚠️ {items.filter(i => +i.actual_quantity < +i.quantity).length} منتج ناقص — إجمالي النقص: <strong>{totalShortage}</strong> قطعة
              </div>
            ) : (
              <div style={{ background:'#dcfce7', border:'1px solid #86efac', borderRadius:8, padding:'10px 14px', fontSize:'0.85rem', color:'#15803d' }}>
                ✅ جميع الكميات مكتملة
              </div>
            )}
          </>)}
        </div>
        <div className="modal-footer">
          {mode === 'confirm' ? (<>
            <button className="btn btn-success" onClick={() => save(true)} disabled={saving || loading}>
              {saving ? 'جاري الحفظ...' : '✅ تأكيد وتحويل لـ "جاهز للتسليم"'}
            </button>
            <button className="btn btn-secondary" onClick={() => save(false)} disabled={saving || loading}>
              💾 حفظ فقط بدون تغيير الحالة
            </button>
          </>) : (
            <button className="btn btn-primary" onClick={() => save(false)} disabled={saving || loading}>
              {saving ? 'جاري الحفظ...' : '💾 حفظ الكميات'}
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose}>إغلاق</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   مودال المواد الناقصة
══════════════════════════════════════════ */
function ShortageModal({ order, onClose }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    API.get(`/orders/${order.id}/shortage-report`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [order.id]);

  const printShortage = () => {
    const win = window.open('', '_blank', 'width=700,height=500');
    const _d=new Date(); const today=`${String(_d.getDate()).padStart(2,'0')}/${String(_d.getMonth()+1).padStart(2,'0')}/${_d.getFullYear()}`;
    const rows = (data?.shortages||[]).map(m => `
      <tr>
        <td><strong>${m.name}</strong></td>
        <td style="text-align:center;color:#d97706;font-weight:800">${m.total_needed}</td>
        <td style="text-align:center">${m.unit}</td>
        <td style="text-align:center;color:#16a34a;font-weight:700">${m.available}</td>
        <td style="text-align:center;color:#dc2626;font-weight:800">${m.shortage}</td>
        <td></td>
      </tr>`).join('');
    win.document.write(`<!DOCTYPE html><html lang="ar" dir="rtl"><head><meta charset="utf-8"/>
      <title>طلب مواد — ${order.order_number}</title>
      <link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&display=swap" rel="stylesheet">
      <style>
        *{margin:0;padding:0;box-sizing:border-box}body{font-family:'Tajawal',sans-serif;direction:rtl;color:#111;padding:20px;font-size:14px}
        .header{display:flex;justify-content:space-between;border-bottom:3px solid #dc2626;padding-bottom:12px;margin-bottom:16px}
        .logo{font-size:1.4rem;font-weight:800;color:#dc2626}.logo small{display:block;font-size:0.75rem;color:#666;font-weight:400}
        table{width:100%;border-collapse:collapse;margin-top:12px}thead{background:#dc2626;color:#fff}
        th{padding:9px 12px;text-align:right;font-size:0.8rem}td{padding:9px 12px;border-bottom:1px solid #f0f0f0;font-size:0.85rem}
        tr:nth-child(even) td{background:#fafafa}
        .footer{margin-top:24px;padding-top:14px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between}
        .sign-box{text-align:center}.sign-line{width:140px;border-top:1px solid #999;margin:32px auto 6px}
        @media print{@page{margin:1cm;size:A4}}
      </style></head><body>
      <div class="header">
        <div class="logo">🌸 Jasmine Perfumes<small>⚠️ طلب توريد مواد ناقصة</small></div>
        <div style="text-align:left"><strong>${order.order_number}</strong><br/><small style="color:#888">${today}</small></div>
      </div>
      <table>
        <thead><tr><th>المادة</th><th style="text-align:center">إجمالي المطلوب</th><th style="text-align:center">الوحدة</th><th style="text-align:center">المتوفر</th><th style="text-align:center">الناقص</th><th>ملاحظات</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="footer">
        <div class="sign-box"><div class="sign-line"></div><p style="font-size:0.75rem;color:#666">توقيع مسؤول المخزون</p></div>
        <div class="sign-box"><div class="sign-line"></div><p style="font-size:0.75rem;color:#666">توقيع المدير</p></div>
      </div></body></html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 600);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">📋 خطة الإنتاج — {order.order_number}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {loading ? <div className="loading"><div className="spinner"/></div> : (<>

            {/* ===== ملخص المنتجات: جاهز vs يحتاج تصنيع ===== */}
            {data?.finished_summary?.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:'0.85rem', marginBottom:8, color:'var(--text)' }}>📦 المنتجات المطلوبة</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {data.finished_summary.map((p, i) => (
                    <div key={i} style={{ background:'#f8f9fb', borderRadius:8, padding:'10px 14px', border:'1px solid var(--border)' }}>
                      <div style={{ fontWeight:700, fontSize:'0.875rem', marginBottom:6 }}>
                        {p.product_name} {p.flavor_name && <span style={{ background:'var(--primary)', color:'white', borderRadius:20, padding:'1px 8px', fontSize:'0.72rem', marginRight:6 }}>🌸 {p.flavor_name}</span>}
                        <span style={{ color:'var(--text-muted)', fontWeight:400, fontSize:'0.8rem', marginRight:8 }}>إجمالي: {p.total_qty} قطعة</span>
                      </div>
                      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                        {p.from_stock > 0 && (
                          <span style={{ background:'#dcfce7', color:'#15803d', borderRadius:6, padding:'3px 10px', fontSize:'0.8rem', fontWeight:700 }}>
                            ✅ {p.from_stock} جاهز من المخزون
                          </span>
                        )}
                        {p.needs_production > 0 && (
                          <span style={{ background:'#fef3c7', color:'#92400e', borderRadius:6, padding:'3px 10px', fontSize:'0.8rem', fontWeight:700 }}>
                            🔧 {p.needs_production} يحتاج تصنيع
                          </span>
                        )}
                        {p.needs_production === 0 && (
                          <span style={{ background:'#dcfce7', color:'#15803d', borderRadius:6, padding:'3px 10px', fontSize:'0.8rem' }}>
                            ✅ كامل الطلب جاهز في المخزون
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ===== المواد الأولية للتصنيع ===== */}
            {data?.all_materials?.length > 0 ? (<>
              <div style={{ fontWeight:700, fontSize:'0.85rem', marginBottom:8, color:'var(--text)' }}>🔧 المواد الأولية اللازمة للتصنيع</div>
              <div style={{ overflowX:'auto', marginBottom:12 }}>
                <table style={{ minWidth:480 }}>
                  <thead>
                    <tr>
                      <th>المادة</th>
                      <th style={{textAlign:'center'}}>مطلوب</th>
                      <th style={{textAlign:'center'}}>متوفر</th>
                      <th style={{textAlign:'center'}}>الحالة</th>
                      <th style={{textAlign:'center'}}>الوحدة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.all_materials.map(m => (
                      <tr key={m.id} style={{ background: !m.sufficient ? '#fff7ed' : undefined }}>
                        <td className="font-bold" style={{ fontSize:'0.85rem' }}>{m.name}</td>
                        <td style={{textAlign:'center', fontWeight:700}}>{+m.total_needed.toFixed(3)}</td>
                        <td style={{textAlign:'center', color:'var(--success)', fontWeight:700}}>{m.available}</td>
                        <td style={{textAlign:'center'}}>
                          {m.sufficient
                            ? <span style={{background:'#dcfce7',color:'#15803d',borderRadius:20,padding:'2px 10px',fontWeight:700,fontSize:'0.8rem'}}>✅ متوفر</span>
                            : <span style={{background:'#fee2e2',color:'var(--danger)',borderRadius:20,padding:'2px 10px',fontWeight:800,fontSize:'0.8rem'}}>⚠️ ناقص {+m.shortage.toFixed(3)}</span>
                          }
                        </td>
                        <td style={{textAlign:'center',color:'var(--text-muted)'}}>{m.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>) : (
              data?.finished_summary?.every(p => p.needs_production === 0) && (
                <div className="alert alert-success">✅ كامل الطلب متوفر في المخزون الجاهز — لا يوجد تصنيع مطلوب</div>
              )
            )}

            {/* ملخص النقص */}
            {data?.has_shortage && (
              <div style={{ background:'#fef3c7', border:'1px solid #fcd34d', borderRadius:8, padding:'10px 14px', fontSize:'0.85rem', color:'#92400e' }}>
                ⚠️ {data.shortages.length} مادة ناقصة — يجب تأمينها قبل بدء الإنتاج
              </div>
            )}
            {!data?.has_shortage && data?.all_materials?.length > 0 && (
              <div style={{ background:'#dcfce7', border:'1px solid #86efac', borderRadius:8, padding:'10px 14px', fontSize:'0.85rem', color:'#15803d' }}>
                ✅ جميع المواد الأولية متوفرة — يمكن بدء التصنيع
              </div>
            )}
          </>)}
        </div>
        <div className="modal-footer">
          {data?.has_shortage && <button className="btn btn-danger" onClick={printShortage}>🖨️ طباعة طلب التوريد</button>}
          <button className="btn btn-secondary" onClick={onClose}>إغلاق</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   مودال الطباعة
══════════════════════════════════════════ */
function PrintModal({ order, onClose }) {
  const [detail,  setDetail]  = useState(null);
  const [bom,     setBom]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get(`/orders/${order.id}`).then(async r => {
      const d = r.data; setDetail(d);
      if (d.items?.length) {
        try {
          const bomRes = await API.post('/bom/calculate', {
            items: d.items.map(i => ({ finished_product_id: i.finished_product_id, flavor_id: i.flavor_id||null, quantity: i.quantity }))
          });
          setBom(bomRes.data.materials || []);
        } catch { setBom([]); }
      }
      setLoading(false);
    });
  }, [order.id]);

  const printPage = (type) => {
    const win = window.open('', '_blank', 'width=860,height=700');
    const _d=new Date(); const today=`${String(_d.getDate()).padStart(2,'0')}/${String(_d.getMonth()+1).padStart(2,'0')}/${_d.getFullYear()}`;
    const css = `*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Tajawal',sans-serif;direction:rtl;color:#111;background:#fff;padding:20px;font-size:14px}
      .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #6c3fc5;padding-bottom:14px;margin-bottom:18px}
      .logo{font-size:1.5rem;font-weight:800;color:#6c3fc5}.logo small{display:block;font-size:0.75rem;color:#666;font-weight:400}
      .info-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:16px}
      .info-box{background:#f8f9fb;border-radius:8px;padding:10px 12px;border:1px solid #e5e7eb}
      .info-box label{font-size:0.7rem;color:#6b7280;display:block;margin-bottom:2px}.info-box strong{font-size:0.9rem}
      h3{font-size:0.95rem;font-weight:800;margin:14px 0 8px;padding-right:8px;border-right:4px solid #6c3fc5}
      table{width:100%;border-collapse:collapse;margin-bottom:14px}thead{background:#6c3fc5;color:#fff}
      th{padding:9px 12px;text-align:right;font-size:0.8rem;font-weight:700}td{padding:9px 12px;border-bottom:1px solid #f0f0f0;font-size:0.85rem;vertical-align:middle}
      tr:nth-child(even) td{background:#fafafa}
      .bold{font-weight:700}.center{text-align:center}
      .big-qty{font-size:1.1rem;font-weight:800;color:#6c3fc5;text-align:center}
      .actual-qty{font-size:1.1rem;font-weight:800;color:#16a34a;text-align:center}
      .shortage td{background:#fff7ed!important}
      .tag-ok{background:#dcfce7;color:#16a34a;border-radius:20px;padding:2px 9px;font-size:0.78rem;font-weight:700}
      .tag-warn{background:#fef3c7;color:#d97706;border-radius:20px;padding:2px 9px;font-size:0.78rem;font-weight:700}
      .flavor{color:#7c3aed;font-weight:700}
      .footer{display:flex;justify-content:space-between;margin-top:24px;padding-top:14px;border-top:1px solid #e5e7eb}
      .sign-box{text-align:center}.sign-line{width:150px;border-top:1px solid #999;margin:36px auto 6px}.sign-box p{font-size:0.78rem;color:#666}
      @media print{body{padding:10px}@page{margin:1cm;size:A4}}`;

    const infoGrid = `<div class="info-grid">
      <div class="info-box"><label>العميل</label><strong>${detail?.customer_name||order.customer_name}</strong></div>
      <div class="info-box"><label>البلد</label><strong>${detail?.customer_country||'-'}</strong></div>
      <div class="info-box"><label>رقم الطلب</label><strong>${order.order_number}</strong></div>
      <div class="info-box"><label>تاريخ الطلب</label><strong>${(() => { const d=new Date(order.created_at||Date.now()); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}</strong></div>
      <div class="info-box"><label>تاريخ التسليم المتوقع</label><strong>${order.expected_delivery_date ? (() => { const d=new Date(order.expected_delivery_date); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })() : '-'}</strong></div>
      <div class="info-box"><label>حالة الإنتاج</label><strong>${PROD_STATUS[order.production_status]||order.production_status}</strong></div>
    </div>`;

    let body = '';
    if (type === 'production') {
      const rows = (detail?.items||[]).map(i => {
        const hasActual = i.actual_quantity != null;
        const shortage  = hasActual && +i.actual_quantity < +i.quantity;
        return `<tr class="${shortage?'shortage':''}">
          <td class="bold">${i.finished_product_name||'-'}</td>
          <td class="flavor center">${i.flavor_name?'🌸 '+i.flavor_name:'-'}</td>
          <td class="big-qty">${i.quantity}</td>
          <td class="${hasActual?(shortage?'tag-warn':'actual-qty'):'center'}" style="${hasActual?'':'color:#ccc'}">
            ${hasActual?i.actual_quantity:'-'}${shortage?' <small style="color:#d97706">(ناقص '+(+i.quantity-+i.actual_quantity)+')</small>':''}
          </td>
          <td>${i.shortage_reason?'<span class="tag-warn">'+i.shortage_reason+'</span>':(hasActual?'<span class="tag-ok">✓</span>':'')}</td>
        </tr>`;
      }).join('');
      body = `<h3>🏭 قائمة الإنتاج</h3>
        <table><thead><tr><th>المنتج</th><th style="text-align:center">الرائحة</th><th style="text-align:center">مطلوب</th><th style="text-align:center">أُنتج فعلاً</th><th>الملاحظات</th></tr></thead>
        <tbody>${rows}</tbody></table>
        ${order.production_notes?`<div style="background:#fffbeb;border:1px solid #fcd34d;border-radius:8px;padding:12px"><strong style="font-size:0.8rem;color:#92400e">📝 ملاحظات:</strong><br/>${order.production_notes}</div>`:''}`;
    } else {
      const rows = bom.length ? bom.map(m => `
        <tr class="${!m.sufficient?'shortage':''}">
          <td class="bold">${m.name}</td><td class="big-qty">${m.total_needed}</td>
          <td class="center">${m.unit}</td>
          <td class="center ${m.sufficient?'tag-ok':'tag-warn'}">${m.available}</td>
          <td class="center">${!m.sufficient?'<span class="tag-warn">⚠️ ناقص '+m.shortage+'</span>':'<span class="tag-ok">✓ كافٍ</span>'}</td>
          <td></td>
        </tr>`).join('') : '<tr><td colspan="6" style="text-align:center;color:#999;padding:20px">لا توجد وصفة BOM</td></tr>';
      body = `<h3>📦 قائمة المواد الأولية — لموظف المخزون</h3>
        <table><thead><tr><th>المادة</th><th style="text-align:center">مطلوب</th><th style="text-align:center">الوحدة</th><th style="text-align:center">متوفر</th><th style="text-align:center">الحالة</th><th>ملاحظات التجميع</th></tr></thead>
        <tbody>${rows}</tbody></table>`;
    }

    win.document.write(`<!DOCTYPE html><html lang="ar" dir="rtl"><head><meta charset="utf-8"/>
      <title>${type==='production'?'ورقة الإنتاج':'ورقة المخزون'} — ${order.order_number}</title>
      <link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&display=swap" rel="stylesheet">
      <style>${css}</style></head><body>
      <div class="header">
        <div class="logo">🌸 Jasmine Perfumes<small>${type==='production'?'🏭 ورقة الإنتاج':'📦 ورقة المخزون'}</small></div>
        <div style="text-align:left"><h2 style="font-size:1.1rem;font-weight:800;color:#6c3fc5">${order.order_number}</h2><p style="font-size:0.78rem;color:#888">${today}</p></div>
      </div>
      ${infoGrid}${body}
      <div class="footer">
        <div class="sign-box"><div class="sign-line"></div><p>${type==='production'?'توقيع مسؤول الإنتاج':'توقيع موظف المخزون'}</p></div>
        <div class="sign-box"><div class="sign-line"></div><p>توقيع المدير</p></div>
        <div style="font-size:0.72rem;color:#aaa;align-self:flex-end">طُبع: ${today}</div>
      </div></body></html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 700);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">🖨️ طباعة — {order.order_number}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {loading ? <div className="loading"><div className="spinner"/></div> : (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div style={{ background:'#f8f9fb', borderRadius:10, padding:'14px 16px', border:'1px solid var(--border)' }}>
                <div style={{ fontWeight:700, marginBottom:4 }}>🏭 ورقة الإنتاج</div>
                <div style={{ fontSize:'0.82rem', color:'var(--text-muted)', marginBottom:10 }}>للمسؤول — المنتجات والكميات المطلوبة والمنتجة</div>
                <button className="btn btn-primary btn-block" onClick={() => printPage('production')}>🖨️ طباعة ورقة الإنتاج</button>
              </div>
              <div style={{ background:'#f8f9fb', borderRadius:10, padding:'14px 16px', border:'1px solid var(--border)' }}>
                <div style={{ fontWeight:700, marginBottom:4 }}>📦 ورقة المخزون</div>
                <div style={{ fontSize:'0.82rem', color:'var(--text-muted)', marginBottom:10 }}>
                  لموظف المخزون — {bom.length} مادة أولية
                  {bom.some(m=>!m.sufficient) && <span style={{ color:'var(--warning)', fontWeight:700 }}> · ⚠️ {bom.filter(m=>!m.sufficient).length} ناقصة</span>}
                </div>
                <button className="btn btn-secondary btn-block" onClick={() => printPage('warehouse')}>🖨️ طباعة ورقة المخزون</button>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>إغلاق</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   تبويب الإنتاج المستقل
══════════════════════════════════════════ */
function IndependentProduction() {
  const [products,      setProducts]      = useState([]);
  const [history,       setHistory]       = useState([]);
  const [search,        setSearch]        = useState('');
  const [form,          setForm]          = useState({ finished_product_id: '', quantity: '', notes: '' });
  const [checking,      setChecking]      = useState(false);
  const [checkResult,   setCheckResult]   = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState('');
  const [success,       setSuccess]       = useState('');
  const [page,          setPage]          = useState(1);
  const PER_PAGE = 50;

  useEffect(() => {
    API.get('/finished-products').then(r => setProducts(r.data.filter(p => p.active)));
    loadHistory();
  }, []);

  const loadHistory = () => {
    API.get('/production-orders').then(r => setHistory(r.data)).catch(() => {});
  };

  // كلما تغير المنتج أو الكمية، امسح نتيجة الفحص
  const handleFormChange = (field, val) => {
    setForm(f => ({ ...f, [field]: val }));
    setCheckResult(null);
    setError('');
    setSuccess('');
  };

  const handleCheck = async () => {
    if (!form.finished_product_id || !form.quantity || +form.quantity <= 0) {
      setError('اختر المنتج وأدخل الكمية أولاً');
      return;
    }
    setChecking(true);
    setError('');
    try {
      const res = await API.post('/production-orders/check', {
        finished_product_id: +form.finished_product_id,
        quantity: +form.quantity
      });
      setCheckResult(res.data);
    } catch(e) {
      setError(e.response?.data?.error || 'حدث خطأ');
    } finally { setChecking(false); }
  };

  const handleProduce = async () => {
    if (!checkResult) return;
    setSaving(true);
    setError('');
    try {
      await API.post('/production-orders', {
        finished_product_id: +form.finished_product_id,
        quantity: +form.quantity,
        notes: form.notes
      });
      setSuccess(`✅ تم تسجيل إنتاج ${form.quantity} قطعة بنجاح وإضافتها للمخزون`);
      setForm({ finished_product_id: '', quantity: '', notes: '' });
      setCheckResult(null);
      loadHistory();
    } catch(e) {
      setError(e.response?.data?.error || 'حدث خطأ');
    } finally { setSaving(false); }
  };

  const selectedProduct = products.find(p => p.id === +form.finished_product_id);
  const filteredHistory = history.filter(h =>
    (h.product_name || '').includes(search) ||
    (h.created_by_name || '').includes(search)
  );
  const pagedHistory = filteredHistory.slice((page-1)*PER_PAGE, page*PER_PAGE);
  useEffect(() => { setPage(1); }, [search]);

  return (
    <div style={{ display:'grid', gridTemplateColumns:'380px 1fr', gap:20, alignItems:'start' }}>

      {/* فورم الإنتاج */}
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ fontWeight:800, marginBottom:16, fontSize:'1rem' }}>🏭 تسجيل إنتاج جديد</h3>

        {error && <div className="alert alert-danger" style={{ marginBottom:12 }}>{error}</div>}
        {success && <div className="alert alert-success" style={{ marginBottom:12 }}>{success}</div>}

        <div className="form-group">
          <label className="form-label">المنتج النهائي *</label>
          <select className="form-control" value={form.finished_product_id}
            onChange={e => handleFormChange('finished_product_id', e.target.value)}>
            <option value="">— اختر المنتج —</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {selectedProduct && (
          <div style={{ background:'var(--surface-2)', borderRadius:8, padding:'8px 12px', marginBottom:12, fontSize:'0.82rem', color:'var(--muted)' }}>
            📦 المخزون الحالي: <strong style={{ color:'var(--brand)' }}>{selectedProduct.stock_qty || 0} قطعة</strong>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">الكمية المنتجة *</label>
          <input type="number" className="form-control" placeholder="0"
            min="1" value={form.quantity}
            onChange={e => handleFormChange('quantity', e.target.value)} />
        </div>

        <div className="form-group">
          <label className="form-label">ملاحظات</label>
          <textarea className="form-control" rows={2} placeholder="ملاحظات اختيارية..."
            value={form.notes} onChange={e => handleFormChange('notes', e.target.value)} />
        </div>

        {/* زر الفحص */}
        {!checkResult && (
          <button className="btn btn-secondary" style={{ width:'100%', marginBottom:8 }}
            onClick={handleCheck} disabled={checking}>
            {checking ? '⏳ جاري الفحص...' : '🔍 فحص توفر المواد'}
          </button>
        )}

        {/* نتيجة الفحص */}
        {checkResult && (
          <div style={{ marginBottom:12 }}>
            <div style={{
              background: checkResult.can_produce ? '#dcfce7' : '#fee2e2',
              border: `1px solid ${checkResult.can_produce ? '#86efac' : '#fca5a5'}`,
              borderRadius:8, padding:'10px 14px', marginBottom:10
            }}>
              <div style={{ fontWeight:700, fontSize:'0.9rem', color: checkResult.can_produce ? '#15803d' : '#dc2626' }}>
                {checkResult.can_produce ? '✅ جميع المواد متوفرة — يمكن الإنتاج' : '❌ توجد مواد ناقصة'}
              </div>
            </div>

            <div style={{ overflowX:'auto', marginBottom:10 }}>
              <table style={{ fontSize:'0.8rem' }}>
                <thead>
                  <tr>
                    <th>المادة</th>
                    <th style={{textAlign:'center'}}>مطلوب</th>
                    <th style={{textAlign:'center'}}>متوفر</th>
                    <th style={{textAlign:'center'}}>الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {checkResult.materials.map((m, i) => (
                    <tr key={i} style={{ background: m.sufficient ? undefined : '#fff7ed' }}>
                      <td>{m.name}</td>
                      <td style={{textAlign:'center', fontWeight:700}}>{m.needed} {m.unit}</td>
                      <td style={{textAlign:'center', color: m.sufficient ? 'var(--success)' : 'var(--danger)', fontWeight:700}}>{m.available}</td>
                      <td style={{textAlign:'center'}}>
                        {m.sufficient
                          ? <span style={{color:'var(--success)'}}>✅</span>
                          : <span style={{color:'var(--danger)',fontWeight:700}}>⚠️ ناقص {(m.needed - m.available).toFixed(2)}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display:'flex', gap:8 }}>
              {checkResult.can_produce && (
                <button className="btn btn-success" style={{ flex:1 }}
                  onClick={handleProduce} disabled={saving}>
                  {saving ? 'جاري التسجيل...' : '✅ تأكيد الإنتاج'}
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => { setCheckResult(null); setError(''); }}>
                ↩ تعديل
              </button>
            </div>
          </div>
        )}
      </div>

      {/* سجل الإنتاج المستقل */}
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <h3 style={{ fontWeight:800, fontSize:'1rem' }}>📋 سجل الإنتاج المستقل</h3>
          <input className="search-input" style={{ width:220 }}
            placeholder="🔍 بحث..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="table-container">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>المنتج</th>
                  <th style={{textAlign:'center'}}>الكمية</th>
                  <th>ملاحظات</th>
                  <th>بواسطة</th>
                  <th>التاريخ</th>
                </tr>
              </thead>
              <tbody>
                {pagedHistory.map(h => (
                  <tr key={h.id}>
                    <td className="font-bold">{h.product_name}</td>
                    <td style={{textAlign:'center'}}>
                      <span style={{background:'var(--brand-light)',color:'var(--brand)',borderRadius:20,padding:'2px 10px',fontWeight:800}}>
                        {h.quantity}
                      </span>
                    </td>
                    <td className="text-muted" style={{fontSize:'0.82rem'}}>{h.notes || '-'}</td>
                    <td className="text-muted">{h.created_by_name}</td>
                    <td className="text-muted">{fmt.date(h.created_at)}</td>
                  </tr>
                ))}
                {filteredHistory.length === 0 && (
                  <tr><td colSpan={5}>
                    <div className="empty-state"><div className="empty-state-icon">🏭</div><p>لا يوجد سجل إنتاج مستقل بعد</p></div>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={page} total={filteredHistory.length} perPage={PER_PAGE} onPage={setPage} />
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   الصفحة الرئيسية
══════════════════════════════════════════ */
export default function Production() {
  const { user } = useAuth();
  const [orders,        setOrders]        = useState([]);
  const [view,          setView]          = useState('list');
  const [notesOrder,    setNotesOrder]    = useState(null);
  const [printOrder,    setPrintOrder]    = useState(null);
  const [actualOrder,   setActualOrder]   = useState(null);
  const [actualMode,    setActualMode]    = useState('edit');
  const [shortageOrder, setShortageOrder] = useState(null);
  const [notes,         setNotes]         = useState('');
  const [pendingIds,    setPendingIds]    = useState(new Set());

  const load = useCallback(() => API.get('/production').then(r => setOrders(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const deleteOrder = async (o) => {
    if (!window.confirm(`حذف الطلب ${o.order_number}؟\nهذا الإجراء لا يمكن التراجع عنه.`)) return;
    try { await API.delete(`/orders/${o.id}`); load(); }
    catch(e) { alert(e.response?.data?.error || 'حدث خطأ'); }
  };

  const updateStatus = async (id, newStatus) => {
    if (pendingIds.has(id)) return;
    setPendingIds(prev => new Set([...prev, id]));
    try {
      await API.put(`/orders/${id}/production`, {
        production_status: newStatus,
        production_notes: orders.find(o=>o.id===id)?.production_notes || ''
      });
      await load();
    } catch(e) {
      alert(e.response?.data?.error || 'حدث خطأ');
    } finally {
      setPendingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleReadyClick = (order) => { setActualOrder(order); setActualMode('confirm'); };
  const handleEditClick  = (order) => { setActualOrder(order); setActualMode('edit'); };
  const byStatus = s => orders.filter(o => o.production_status === s);

  const ActionButtons = ({ o }) => {
    const isPending = pendingIds.has(o.id);
    const hasBomWarning = o.missing_bom && o.missing_bom.length > 0;
    return (
      <div className="td-actions">
        {/* تنبيه BOM ناقص */}
        {hasBomWarning && o.production_status !== 'delivered' && (
          <span title={`منتجات بدون وصفة: ${o.missing_bom.join('، ')}`}
            style={{ cursor:'help', fontSize:'1rem' }}>⚠️</span>
        )}
        {/* حالة الحجز */}
        {o.is_reserved && (
          <span className="reserved-badge" title="المواد محجوزة من المخزون">🔒 محجوز</span>
        )}
        {o.production_status === 'pending' && (
          <button className="btn btn-sm btn-primary" disabled={isPending || hasBomWarning}
            title={hasBomWarning ? `لا يمكن البدء — وصفات ناقصة: ${o.missing_bom.join('، ')}` : 'بدء الإنتاج'}
            onClick={() => updateStatus(o.id, 'in_progress')}>
            {isPending ? '...' : '▶ بدء'}
          </button>
        )}
        {o.production_status === 'in_progress' && (
          <button className="btn btn-sm btn-success" disabled={isPending}
            onClick={() => handleReadyClick(o)}>
            {isPending ? '...' : '✓ جاهز'}
          </button>
        )}
        {o.production_status === 'ready' && (
          <button className="btn btn-sm btn-primary" disabled={isPending}
            onClick={() => updateStatus(o.id, 'delivered')}>
            {isPending ? '...' : '📦 تسليم'}
          </button>
        )}
        <button className="btn btn-sm btn-secondary" title="تعديل الكميات" onClick={() => handleEditClick(o)}>✏️</button>
        <button className="btn btn-sm btn-secondary" title="مواد ناقصة"    onClick={() => setShortageOrder(o)}>⚠️</button>
        <button className="btn btn-sm btn-secondary" title="طباعة"          onClick={() => setPrintOrder(o)}>🖨️</button>
        <button className="btn btn-sm btn-secondary" title="ملاحظات"        onClick={() => { setNotesOrder(o); setNotes(o.production_notes||''); }}>📝</button>
        {user?.role === 'admin' && (
          <button className="btn btn-sm btn-danger" title="حذف الطلب" onClick={() => deleteOrder(o)}>🗑️</button>
        )}
      </div>
    );
  };

  const pending_count    = orders.filter(o => o.production_status === 'pending').length;
  const inprog_count     = orders.filter(o => o.production_status === 'in_progress').length;
  const ready_count      = orders.filter(o => o.production_status === 'ready').length;
  const bom_warn_count   = orders.filter(o => o.missing_bom?.length > 0 && o.production_status !== 'delivered').length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">لوحة الإنتاج</h1>
          <div className="page-title-sub">{orders.length} طلب إجمالي</div>
        </div>
        <div className="tabs" style={{ margin:0 }}>
          <button className={`tab ${view==='list'?'active':''}`}        onClick={()=>setView('list')}>قائمة</button>
          <button className={`tab ${view==='kanban'?'active':''}`}      onClick={()=>setView('kanban')}>كانبان</button>
          <button className={`tab ${view==='independent'?'active':''}`} onClick={()=>setView('independent')}>إنتاج مستقل</button>
        </div>
      </div>

      {/* Stats Row */}
      {view !== 'independent' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
          {[
            { label:'بانتظار البدء', count: pending_count,  color:'var(--warning)',  bg:'var(--warning-bg)' },
            { label:'قيد التنفيذ',  count: inprog_count,   color:'var(--info)',     bg:'var(--info-bg)' },
            { label:'جاهز للتسليم',count: ready_count,    color:'var(--success)',  bg:'var(--success-bg)' },
            { label:'وصفات ناقصة', count: bom_warn_count, color:'var(--danger)',   bg:'var(--danger-bg)' },
          ].map((s,i) => (
            <div key={i} style={{
              background:'var(--surface)', border:`1.5px solid ${s.count > 0 ? s.color : 'var(--border)'}`,
              borderRadius:12, padding:'14px 18px',
              display:'flex', justifyContent:'space-between', alignItems:'center'
            }}>
              <span style={{ fontSize:'0.82rem', fontWeight:600, color: s.count > 0 ? s.color : 'var(--muted)' }}>{s.label}</span>
              <span style={{ fontSize:'1.4rem', fontWeight:800, color: s.count > 0 ? s.color : 'var(--muted)' }}>{s.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* الموداليات */}
      {printOrder    && <PrintModal    order={printOrder}    onClose={() => setPrintOrder(null)} />}
      {shortageOrder && <ShortageModal order={shortageOrder} onClose={() => setShortageOrder(null)} />}
      {actualOrder   && (
        <ActualQtyModal order={actualOrder} mode={actualMode}
          onClose={() => setActualOrder(null)} onSaved={load} />
      )}

      {/* مودال الملاحظات */}
      {notesOrder && (
        <div className="modal-overlay" onClick={() => setNotesOrder(null)}>
          <div className="modal" style={{ maxWidth:480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">📝 ملاحظات — {notesOrder.order_number}</span>
              <button className="modal-close" onClick={() => setNotesOrder(null)}>✕</button>
            </div>
            <div className="modal-body">
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} placeholder="ملاحظات الإنتاج..." />
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={async () => {
                await API.put(`/orders/${notesOrder.id}/production`, { production_status: notesOrder.production_status, production_notes: notes });
                setNotesOrder(null); load();
              }}>💾 حفظ</button>
              <button className="btn btn-ghost" onClick={() => setNotesOrder(null)}>إغلاق</button>
            </div>
          </div>
        </div>
      )}

      {/* تبويب الإنتاج المستقل */}
      {view === 'independent' && <IndependentProduction />}

      {/* قائمة */}
      {view === 'list' && (
        <div className="table-container">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>رقم الطلب</th><th>العميل</th>
                  <th>التسليم المتوقع</th>
                  <th>الحالة</th><th>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} style={{ background: o.missing_bom?.length > 0 && o.production_status !== 'delivered' ? '#fffbeb' : undefined }}>
                    <td className="font-bold">{o.order_number}</td>
                    <td>
                      <div className="font-bold">{o.customer_name}</div>
                      {o.missing_bom?.length > 0 && o.production_status !== 'delivered' && (
                        <div style={{ fontSize:'0.73rem', color:'#d97706', marginTop:2, fontWeight:600 }}>
                          ⚠️ وصفة ناقصة: {o.missing_bom.join('، ')}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize:'0.82rem' }}>
                      {o.expected_delivery_date
                        ? <span style={{ color: new Date(o.expected_delivery_date) < new Date() ? 'var(--danger)' : 'var(--text)', fontWeight: new Date(o.expected_delivery_date) < new Date() ? 700 : 400 }}>{fmt.date(o.expected_delivery_date)}</span>
                        : <span style={{ color:'#d1d5db' }}>غير محدد</span>}
                    </td>
                    <td>
                      <div className="prod-status-row">
                        <span className={`badge ${STATUS_COLOR[o.production_status]}`}>{PROD_STATUS[o.production_status]}</span>
                        {o.is_reserved && <span className="reserved-badge">🔒</span>}
                      </div>
                    </td>
                    <td><ActionButtons o={o} /></td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan={5}>
                    <div className="empty-state"><div className="empty-state-icon">🏭</div><p>لا توجد طلبات للإنتاج</p></div>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* كانبان */}
      {view === 'kanban' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 }}>
          {STATUS_COLS.map(status => (
            <div key={status}>
              <div className="kanban-col-header" style={{
                background: status==='in_progress' ? 'var(--brand-light)' :
                            status==='pending' ? 'var(--info-bg)' :
                            status==='ready' ? 'var(--success-bg)' : 'var(--surface-3)',
                border: `1.5px solid ${status==='in_progress' ? 'var(--brand)' : status==='ready' ? 'var(--success)' : 'var(--border)'}`
              }}>
                <span>{PROD_STATUS[status]}</span>
                <span className={`badge ${STATUS_COLOR[status]}`}>{byStatus(status).length}</span>
              </div>
              {byStatus(status).map(o => {
                const isPending = pendingIds.has(o.id);
                return (
                  <div key={o.id}
                    className={`kanban-card${o.missing_bom?.length > 0 && status !== 'delivered' ? ' has-bom-warning' : ''}${o.is_reserved ? ' is-reserved' : ''}`}>
                    <div className="flex-between" style={{ marginBottom:6 }}>
                      <span className="font-bold" style={{ fontSize:'0.9rem' }}>{o.order_number}</span>
                      <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                        {o.is_reserved && <span className="reserved-badge">🔒</span>}
                        <button className="btn btn-sm btn-secondary btn-icon" onClick={() => setPrintOrder(o)}>🖨️</button>
                      </div>
                    </div>
                    <div style={{ fontWeight:600, fontSize:'0.82rem', marginBottom:3 }}>{o.customer_name}</div>
                    <div className="text-muted" style={{ fontSize:'0.75rem', marginBottom:6 }}>
                      {o.items_summary?.length>50 ? o.items_summary.slice(0,50)+'...' : o.items_summary}
                    </div>
                    {o.missing_bom?.length > 0 && status !== 'delivered' && (
                      <div style={{ background:'#fef3c7', borderRadius:6, padding:'5px 8px', fontSize:'0.72rem', color:'#92400e', fontWeight:600, marginBottom:8 }}>
                        ⚠️ وصفة ناقصة: {o.missing_bom.slice(0,2).join('، ')}{o.missing_bom.length > 2 ? ` +${o.missing_bom.length-2}` : ''}
                      </div>
                    )}
                    {o.expected_delivery_date && (
                      <div style={{ fontSize:'0.72rem', color: new Date(o.expected_delivery_date) < new Date() ? 'var(--danger)' : 'var(--text-muted)', marginBottom:8 }}>
                        📅 {o.expected_delivery_date}
                      </div>
                    )}
                    <div style={{ display:'flex', gap:5 }}>
                      {status === 'pending' && (
                        <button className="btn btn-sm btn-primary" style={{ flex:1 }}
                          disabled={isPending || (o.missing_bom?.length > 0)}
                          title={o.missing_bom?.length > 0 ? 'وصفة إنتاج ناقصة' : ''}
                          onClick={() => updateStatus(o.id, 'in_progress')}>
                          {isPending ? '...' : '▶ بدء'}
                        </button>
                      )}
                      {status === 'in_progress' && (
                        <button className="btn btn-sm btn-success" style={{ flex:1 }} disabled={isPending}
                          onClick={() => handleReadyClick(o)}>
                          {isPending ? '...' : '✓ جاهز'}
                        </button>
                      )}
                      {status === 'ready' && (
                        <button className="btn btn-sm btn-primary" style={{ flex:1 }} disabled={isPending}
                          onClick={() => updateStatus(o.id, 'delivered')}>
                          {isPending ? '...' : '📦 تسليم'}
                        </button>
                      )}
                      <button className="btn btn-sm btn-secondary btn-icon" title="تعديل الكميات" onClick={() => handleEditClick(o)}>✏️</button>
                    </div>
                  </div>
                );
              })}
              {byStatus(status).length === 0 && (
                <div style={{ textAlign:'center', color:'var(--muted)', fontSize:'0.82rem', padding:'20px 0', opacity:0.5 }}>لا توجد طلبات</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
